import React, { useId, useRef, useState, useCallback } from "react";
import "../../styles/components/_tooltip-button.scss";

/**
 * TooltipButton
 *
 * Automatically repositions the tooltip so it never overflows the viewport.
 * Checks all four edges and chooses the best placement from:
 *   vertical: "above" (default) | "below"
 *   horizontal: "center" | "left" | "right"
 *
 * The arrow always points at the trigger button regardless of placement.
 */
export default function TooltipButton({
    label = "Hover for info",
    onClick,
    disabled = false,
    tooltipTitle = "Important information",
    tooltipBody = "This is a tooltip with detailed information.",
    tooltipIcon,
    className,
    buttonType = "button",
}) {
    const tooltipId = useId();
    const buttonRef = useRef(null);
    const tooltipRef = useRef(null);

    // placement drives which CSS modifier classes are applied
    const [placement, setPlacement] = useState({
        vertical: "above", // "above" | "below"
        horizontal: "center", // "center" | "left" | "right"
        arrowLeft: "50%", // CSS left value for the arrow
        shiftX: 0, // px shift applied to the tooltip panel
    });

    const reposition = useCallback(() => {
        const trigger = buttonRef.current;
        const tooltip = tooltipRef.current;
        if (!trigger || !tooltip) return;

        const tr = trigger.getBoundingClientRect();
        const tw = tooltip.offsetWidth || 280;
        const th = tooltip.offsetHeight || 120;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const gap = 10; // px gap between trigger and tooltip
        const gutter = 8; // min distance from viewport edge

        // ── Vertical: prefer above, fall back to below ──────────────────────
        const spaceAbove = tr.top;
        const spaceBelow = vh - tr.bottom;
        const vertical = spaceAbove >= th + gap + gutter ? "above" : "below";

        // ── Horizontal: try to centre, clamp to viewport ─────────────────────
        // Ideal left edge if tooltip were centred on trigger
        const idealLeft = tr.left + tr.width / 2 - tw / 2;

        let clampedLeft = idealLeft;
        if (clampedLeft < gutter) clampedLeft = gutter;
        if (clampedLeft + tw > vw - gutter) clampedLeft = vw - gutter - tw;

        // Shift is how far we had to move from ideal (used to counter-move arrow)
        const shiftX = clampedLeft - idealLeft;

        // Horizontal class: left/right anchoring only affects CSS fallback;
        // the real work is done via shiftX
        let horizontal = "center";
        if (tr.left + tr.width / 2 < vw / 3) horizontal = "left";
        else if (tr.left + tr.width / 2 > (vw * 2) / 3) horizontal = "right";

        // Arrow left relative to the tooltip panel — points at trigger centre
        const triggerCentreX = tr.left + tr.width / 2;
        const arrowLeftAbsolute = triggerCentreX - clampedLeft;
        // Clamp arrow so it stays within panel (with a border-radius buffer)
        const arrowLeftClamped = Math.min(
            Math.max(arrowLeftAbsolute, 18),
            tw - 18
        );
        const arrowLeft = `${arrowLeftClamped}px`;

        setPlacement({ vertical, horizontal, arrowLeft, shiftX });
    }, []);

    const modClass = `tooltip-button__tooltip--${placement.vertical}--${placement.horizontal}`;

    return (
        <div className="tooltip-button">
            <button
                type={buttonType}
                className={className}
                onClick={onClick}
                disabled={disabled}
                aria-describedby={tooltipId}
                ref={buttonRef}
                onMouseEnter={reposition}
                onFocus={reposition}
            >
                {label}
            </button>

            <div
                id={tooltipId}
                role="tooltip"
                className={`tooltip-button__tooltip ${modClass}`}
                ref={tooltipRef}
                style={{
                    "--tb-shift-x": `${placement.shiftX}px`,
                    "--tb-arrow-left": placement.arrowLeft,
                }}
            >
                <div className="tooltip-button__panel">
                    <div className="tooltip-button__panel-header">
                        <div
                            className="tooltip-button__panel-badge"
                            aria-hidden="true"
                        >
                            {tooltipIcon || <InfoFillIcon />}
                        </div>
                        <span className="tooltip-button__panel-title">
                            {tooltipTitle}
                        </span>
                    </div>
                    <p className="tooltip-button__panel-text">{tooltipBody}</p>
                    <div
                        className="tooltip-button__panel-arrow"
                        aria-hidden="true"
                    />
                </div>
            </div>
        </div>
    );
}

function InfoFillIcon() {
    return (
        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
            />
        </svg>
    );
}
